import IBAN from 'iban'
import { t } from 'i18next'
import { NAME_MAX_LENGTH, PIN_LENGTH, REFERENCE_MAX_LENGTH } from './constants'

/**
 * SEPA reference permitted characters, ported verbatim from goldfish banking's
 * `ReferenceMessage` (which copied it from the backend `RegexUtilities`).
 */
const REFERENCE_PATTERN = /^[a-z0-9():,.+?\s'/-]{0,135}$/i

const TWO_DECIMALS = /^\d+([.,]\d{1,2})?$/

export const normalizeIban = (raw: string): string =>
  raw.replace(/\s/g, '').toUpperCase()

/** Parse a user-entered amount ("1,50" or "1.50") to a number. */
export const parseAmount = (raw: string): number =>
  Number.parseFloat(raw.replace(',', '.'))

export const validateName = (name: string): string | null => {
  const trimmed = name.trim()
  if (!trimmed) return t('validation.nameRequired')
  if (trimmed.length > NAME_MAX_LENGTH)
    return t('validation.nameTooLong', { max: NAME_MAX_LENGTH })
  return null
}

export const validateIban = (iban: string): string | null => {
  const value = normalizeIban(iban)
  if (!value) return t('validation.ibanRequired')
  if (!IBAN.isValid(value)) return t('validation.ibanInvalid')
  return null
}

export const validateAmount = (amount: string): string | null => {
  const trimmed = amount.trim()
  const value = parseAmount(trimmed)
  if (!trimmed || Number.isNaN(value) || value <= 0)
    return t('validation.amountRequired')
  if (!TWO_DECIMALS.test(trimmed)) return t('validation.amountDecimals')
  return null
}

/** Reference is optional; only the contents are constrained. */
export const validateReference = (reference: string): string | null => {
  if (reference.length > REFERENCE_MAX_LENGTH)
    return t('validation.referenceTooLong', { max: REFERENCE_MAX_LENGTH })
  if (!REFERENCE_PATTERN.test(reference))
    return t('validation.referenceInvalidChars')
  return null
}

// Real validation happens server-side via POST /api/payment (Cerium); this
// only checks shape so the API isn't called with an obviously-incomplete PIN.
export const validatePin = (pin: string): string | null => {
  if (pin.length !== PIN_LENGTH)
    return t('validation.pinLength', { count: PIN_LENGTH })
  return null
}
